import React, {useState, useRef, useEffect, useImperativeHandle} from 'react';
import {createPortal} from 'react-dom';
import post from '../utils/post.js';
import {APP_VERSION} from '../utils/appVersion.js';
import PaymentNew from './PaymentNew.js';
import PaymentEdit from './PaymentEdit.js';
import ChartColumn from './ChartColumn.js';
import MonitorAthDonut, {athToneClass} from './MonitorAthDonut.js';
import FormSelect from './FormSelect.js';

const formatSignedAmount = (item) => `${parseInt(item.TYPEID) === 2 ? '-' : ''}${item.AMOUNTS}`;

// Prázdné hodnoty karet „Výkon", dokud nedojdou data z /payment.
const ATH_EMPTY = {
	current_val: 0,
	month_amount_label: '0,00', month_rank_label: '', to_ath_label: '',
	ath_amount_label: '—', ath_month_label: '',
	next_level_label: '—', next_level_remaining_label: '',
	eshop_label: '0,00', entry_label: '0,00',
	ath_ref_val: 0, ath_ref_label: '—', ath_pct_label: '—',
};

// Sdílený obsah sekce Platby – používá ho stránka /payment i popup s platbami
// uživatele v monitoringu. Hledaný výraz je řízený zvenku (stránka ho drží
// v horní liště, popup ho předvyplňuje jménem uživatele), klik na sloupec grafu
// ho mění přes onChangeSearch. Tlačítko „Nový" leží u kontejneru, proto se nová
// platba otevírá přes ref (openNew).
const PaymentList = React.forwardRef(({usrInfo, searchValue = '', onChangeSearch, onLoadingChange}, ref) => {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [ath, setAth] = useState(ATH_EMPTY);
	const [dataUser, setDataUser] = useState([]);
	const [PAYMENT_CHCK1, setPAYMENT_CHCK1] = useState(1);
	const [PAYMENT_CHCK2, setPAYMENT_CHCK2] = useState(1);
	const [, setReport_pdf] = useState('');
	const [data_pluss, setData_pluss] = useState(0);
	const [data_minuss, setData_minuss] = useState(0);
	const [data_kasa, setData_kasa] = useState(0);
	const [data_kasas, setData_kasas] = useState(0);
	const [data_graph_plus, setData_graph_plus] = useState([]);
	const [, setData_graph_minus] = useState([]);
	const [data_graph_plus_colors, setData_graph_plus_colors] = useState([]);
	const [, setData_graph_minus_colors] = useState([]);
	const [data_grapt_labels, setData_grapt_labels] = useState([]);
	const [, setOffset] = useState(0);
	const [loadedCnt, setLoadedCnt] = useState(0);
	const [showNew, setShowNew] = useState(false);
	const [showEdit, setShowEdit] = useState(false);
	const [detailData, setDetailData] = useState([]);
	const [dataNew, setDataNew] = useState([]);
	const [months, setMonths] = useState(24);
	const [monthsName, setMonthsName] = useState('Graf 24 měsíců (2 roky)');
	const [monthsOptions, setMonthsOptions] = useState([]);

	const monthsRef = useRef(null);

	useEffect(() => {
		if (onLoadingChange) {
			onLoadingChange(loading);
		}
	}, [loading, onLoadingChange]);

	// Změna hledání → načteme vždy od začátku.
	useEffect(() => {
		setOffset(0);
		getData(searchValue, 0, months);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchValue]);

	useEffect(() => {
		setMonthsOptions([
			{ID: 120, NAME: 'Graf 120 měsíců (10 let)'},
			{ID: 60, NAME: 'Graf 60 měsíců (5 let)'},
			{ID: 48, NAME: 'Graf 48 měsíců (4 roky)'},
			{ID: 36, NAME: 'Graf 36 měsíců (3 roky)'},
			{ID: 24, NAME: 'Graf 24 měsíců (2 roky)'},
			{ID: 12, NAME: 'Graf 12 měsíců (1 rok)'},
		]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const loadNext = () => {
		setOffset((prevOffset) => {
			let newOffset = prevOffset + usrInfo.LIST_ROWS;
			getData(searchValue, prevOffset + usrInfo.LIST_ROWS, months);
			return newOffset;
		});
	};

	const getData = (search, offset, months) => {
		setLoading(true);

		post('getListPayment', {search: search, offset: offset, months: months})
			.then((res) => {
				setPAYMENT_CHCK1(res.data.PAYMENT_CHCK1);
				setPAYMENT_CHCK2(res.data.PAYMENT_CHCK2);
				setDataUser(res.data.data_user);
				setReport_pdf(res.data.report_pdf);
				setData_pluss(res.data.data_pluss);
				setData_minuss(res.data.data_minuss);
				setData_kasa(res.data.data_kasa);
				setData_kasas(res.data.data_kasas);
				setData_graph_plus(res.data.data_graph_plus);
				setData_graph_minus(res.data.data_graph_minus);
				setData_grapt_labels(res.data.data_graph_labels);
				setData_graph_plus_colors(res.data.data_graph_plus_colors);
				setData_graph_minus_colors(res.data.data_graph_minus_colors);
				setAth(res.data.data_ath);

				if (offset === 0) {
					setData(res.data.data);
				} else {
					setData((prevRows) => {
						const updatedRows = [...prevRows, ...res.data.data];
						return updatedRows;
					});
				}

				setLoadedCnt(res.data.data.length);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const saveFltr = (fltr) => {
		let paymentChecks = {
			PAYMENT_CHCK1: PAYMENT_CHCK1,
			PAYMENT_CHCK2: PAYMENT_CHCK2,
		};

		let fltrvar = 'PAYMENT_CHCK' + fltr;
		let fltrval = paymentChecks[fltrvar];

		if (parseInt(fltrval) === 1) {
			fltrval = 0;
		} else {
			fltrval = 1;
		}

		if (fltr === 1) {
			setPAYMENT_CHCK1(fltrval);
		} else {
			setPAYMENT_CHCK2(fltrval);
		}

		setLoading(true);

		post('savePaymentFltr', {search: searchValue, offset: 0, fltr: fltrvar, fltrval: fltrval, months: months})
			.then((res) => {
				setLoading(false);
				setOffset(0);
				setData_pluss(res.data.data_pluss);
				setData_minuss(res.data.data_minuss);
				setData_kasa(res.data.data_kasa);
				setData_kasas(res.data.data_kasas);
				setData(res.data.data);
				setData_graph_plus(res.data.data_graph_plus);
				setData_graph_minus(res.data.data_graph_minus);
				setData_grapt_labels(res.data.data_graph_labels);
				setData_graph_plus_colors(res.data.data_graph_plus_colors);
				setData_graph_minus_colors(res.data.data_graph_minus_colors);
				setLoadedCnt(res.data.data.length);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	// Klik na sloupec grafu → hledání podle měsíce (kontejner ho propíše i do pole hledání).
	const selectedColumn = (type, value, label) => {
		onChangeSearch(label);
	};

	const handleNew = () => {
		if (usrInfo.usrId === 1) {
			setDataNew({});
			setShowNew(true);
		}
	};

	useImperativeHandle(ref, () => ({openNew: handleNew}));

	const closeNew = () => {
		setShowNew(false);
	};

	const updateRow = (res) => {
		setOffset(0);
		setMonths(24);
		setMonthsName('Graf 24 měsíců (2 roky)');

		setData_pluss(res.data_pluss);
		setData_minuss(res.data_minuss);
		setData_kasa(res.data_kasa);
		setData_kasas(res.data_kasas);
		setData(res.data);
		setData_graph_plus(res.data_graph_plus);
		setData_graph_minus(res.data_graph_minus);
		setData_grapt_labels(res.data_graph_labels);
		setData_graph_plus_colors(res.data_graph_plus_colors);
		setData_graph_minus_colors(res.data_graph_minus_colors);
		setLoadedCnt(res.data.length);

		setAth(res.data_ath);
	};

	const handleEdit = (item) => {
		if (usrInfo.sysAdmin === 1) {
			setDetailData({item: item, user: dataUser});
			setShowEdit(true);
		}
	};

	const closeEdit = () => {
		setShowEdit(false);
	};

	const copyToNewPay = () => {
		let nData = {
			usrId: detailData.item.USRID,
			note: detailData.item.NOTE,
			amount: detailData.item.AMOUNT,
		};

		setDataNew(nData);
		setShowNew(true);
	};

	const setMonth = (val) => {
		setMonths(val.ID);
		setMonthsName(val.NAME);
		getData(searchValue, 0, val.ID);
	};

	const handleOpenPdf = async (url) => {
		setLoading(true);

		try {
			const res = await fetch(url, {
				headers: {Authorization: `Bearer ${usrInfo.token}`, 'X-App-Version': APP_VERSION}
			});

			if (!res.ok) {
				throw new Error(`PDF generation failed with status ${res.status}`);
			}

			const blob = await res.blob();
			const pdfUrl = URL.createObjectURL(blob);
			window.open(pdfUrl, '_blank');
			setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleRowClick = (item) => {
		handleEdit(item);
	};

	// Popisek koláče „Tržba / ATH" – Apex tooltip je vypnutý, detail nese title karty (jako v monitoringu).
	const athShareTitle = (Number(ath.ath_ref_val) || 0) > 0
		? `Tržba tohoto měsíce vůči rekordu: ${ath.month_amount_label} / ${ath.ath_ref_label}${(Number(ath.current_val) || 0) >= (Number(ath.ath_ref_val) || 0) ? ' – rekord překonán' : ''}`
		: 'Zatím není s čím porovnávat – chybí dřívější měsíc s tržbou';

	// Karty stejné jako KPI v monitoringu: ikonka vpravo nahoře, doplněk v závorce za názvem
	// a v hodnotě jen částka na jednom řádku.
	// 1. řádek: celkem za měsíc, z toho eshop a co zbylo čistě na vstupy.
	// 2. řádek: další level, ATH a podíl aktuální tržby na rekordu (koláč).
	const performanceCards = [
		{
			label: 'Aktuální měsíc',
			meta: ath.month_rank_label,
			value: ath.month_amount_label,
			icon: 'month',
			tone: athToneClass(ath.current_val, ath.ath_ref_val),
			title: ath.to_ath_label ? `Do ATH zbývá: ${ath.to_ath_label}` : 'Aktuální měsíc je rekordní',
		},
		{label: 'Eshop', value: ath.eshop_label, icon: 'eshop', title: 'Nákupy v eshopu za aktuální měsíc'},
		{label: 'Čistě za vstupy', value: ath.entry_label, icon: 'entry', title: 'Tržba aktuálního měsíce bez eshopu'},
		{
			label: 'Další level',
			value: ath.next_level_remaining_label ? `-${ath.next_level_remaining_label}` : ath.next_level_label,
			icon: 'level',
			title: ath.next_level_remaining_label ? `Nejbližší vyšší měsíc: ${ath.next_level_label}` : 'Vyšší měsíc už není – aktuální měsíc vede',
		},
		{label: 'ATH', meta: ath.ath_month_label, value: ath.ath_amount_label, icon: 'ath', tone: 'home-summary-card--warning'},
		{label: 'Tržba / ATH', value: ath.ath_pct_label, title: athShareTitle, donut: true},
	];

	const financeCards = [
		{label: 'Příjmy celkem', value: data_pluss, icon: 'income', tone: 'home-summary-card--success'},
		{label: 'Výdaje celkem', value: data_minuss, icon: 'expense', tone: 'home-summary-card--danger'},
		{label: 'KASA', value: data_kasas, icon: 'kasa', tone: data_kasa >= 0 ? 'home-summary-card--success' : 'home-summary-card--danger'},
	];

	const renderSummaryCard = (card) => (
		<div className={`home-summary-card${card.tone ? ' ' + card.tone : ''} payment-page__kpi${card.donut ? ' payment-page__kpi--athshare' : ''}`} key={card.label} title={card.title || ''}>
			{card.icon ? <img className={`payment-page__kpi-icon payment-page__kpi-icon--${card.icon}`} alt="" /> : null}
			<div className="home-summary-card__content">
				<span className="home-summary-card__eyebrow">
					{card.label}
					{card.meta ? <span className="payment-page__kpi-meta"> ({card.meta})</span> : null}
				</span>
				<span className="home-summary-card__value">{card.value}</span>
			</div>
			{card.donut ? (
				<div className="payment-page__kpi-donut" aria-hidden="true">
					<MonitorAthDonut value={ath.current_val} target={ath.ath_ref_val} />
				</div>
			) : null}
		</div>
	);

	const list = data?.map((item, index) => {
		const isExpense = parseInt(item.TYPEID) === 2;
		const canOpenPdf = usrInfo.sysAdmin === 1 && item.USRID > 0;

		return (
			<article className={`payment-page__row${usrInfo.sysAdmin === 1 ? ' payment-page__row--clickable' : ''}`} key={'item_' + index} onClick={() => handleRowClick(item)}>
				<div className="payment-page__row-main">
					<div className="payment-page__row-head">
						<div className="payment-page__row-date">{item.PAYMENTDTS}</div>
						{item.USRNAME !== '' ? <div className="payment-page__row-user">{item.USRNAME}</div> : ''}
					</div>
					{item.NOTE !== '' ? <div className="payment-page__row-note">{item.NOTE}</div> : ''}
				</div>

				<div className="payment-page__row-side">
					<div className={`payment-page__row-amount payment-page__row-amount--${isExpense ? 'negative' : 'positive'}`}>
						{formatSignedAmount(item)}
					</div>

					{canOpenPdf ? (
						<button
							type="button"
							className="payment-page__pdf-button"
							onClick={(event) => {
								event.stopPropagation();
								handleOpenPdf(`/rest/myPaymentPdf?ID=${item.ID}`);
							}}
						>
							<img className="icon1 pdf" alt="" />
							<span>PDF</span>
						</button>
					) : ''}
				</div>
			</article>
		);
	});

	return (
		<>
			{/* Popupy nové / upravované platby jdou přes portál do body – v popupu z monitoringu
			    by je jinak rodičovský popup (backdrop-filter, animace) uzavřel do svého obdélníku. */}
			{createPortal(
				<>
					<PaymentNew showNew={showNew} closeNew={closeNew} updateRow={updateRow} dataNew={dataNew} dataUser={dataUser} />
					<PaymentEdit showEdit={showEdit} closeEdit={closeEdit} updateRow={updateRow} data={detailData} copyToNewPay={copyToNewPay} searchValue={searchValue} />
				</>,
				document.body,
			)}

			<div className="payment-page__shell">
				<section className="payment-page__section">
					<div className="payment-page__section-head payment-page__section-head--stacked">
						<div className="payment-page__toolbar">
							<button type="button" className={`payment-page__filter-chip${PAYMENT_CHCK1 === 1 ? ' is-active is-positive' : ''}`} onClick={() => saveFltr(1)}>
								<span className="payment-page__filter-dot"></span>
								Příjmy
							</button>
							<button type="button" className={`payment-page__filter-chip${PAYMENT_CHCK2 === 1 ? ' is-active is-negative' : ''}`} onClick={() => saveFltr(2)}>
								<span className="payment-page__filter-dot"></span>
								Výdaje
							</button>
							<div className="payment-page__select-wrap">
								<FormSelect type="text" label="" value={months} valueLabel={monthsName} setValue={setMonth} options={monthsOptions} showNone={false} ref={monthsRef} error="" />
							</div>
							<button type="button" className="payment-page__pdf-export" onClick={() => handleOpenPdf(`/rest/paymentPdf?search=${searchValue}`)}>
								<img className="icon1 pdf2" alt="" />
								<span>Přehled</span>
							</button>
						</div>
					</div>
					<div className="payment-page__chart-wrap">
						<ChartColumn data1={data_graph_plus} labels={data_grapt_labels} colorPlus={data_graph_plus_colors} stepSize={5000} linesColor={'#aaaaaa'} selectedColumn={selectedColumn} />
					</div>
				</section>

				<section className="payment-page__section payment-page__section--hero">
					<div className="payment-page__summary-groups">
						<div className="payment-page__summary-group">
							<div className="payment-page__summary-group-title">Výkon</div>
							<div className="payment-page__summary-grid payment-page__summary-grid--performance">
								{performanceCards.map((card) => renderSummaryCard(card))}
							</div>
						</div>
						<div className="payment-page__summary-group payment-page__summary-group--finance">
							<div className="payment-page__summary-group-title">Tok peněz</div>
							<div className="payment-page__summary-grid payment-page__summary-grid--finance">
								{financeCards.map((card) => renderSummaryCard(card))}
							</div>
						</div>
					</div>
				</section>

				<section className="payment-page__section">
					<div className="payment-page__list">
						{list.length > 0 ? list : <div className="payment-page__empty">Zatím nejsou k dispozici žádné platební záznamy.</div>}
					</div>

					<div className="payment-page__load-more">
						{parseInt(loadedCnt) === parseInt(usrInfo.LIST_ROWS) ? (
							<button className="button small loadNext payment-page__button" onClick={() => loadNext()}>
								Načíst další
							</button>
						) : (
							<span className="fnt_12 loadNext payment-page__load-all">Byly načteny všechny záznamy</span>
						)}
					</div>
				</section>
			</div>
		</>
	);
});

export default PaymentList;
